import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Activity } from '../../../model/activity.model';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import { RequirementService } from 'src/app/services/requirement.service';
import { RequirementTypesService } from '@services/requerimenttypes.service';
import { NgbDateStruct, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { DateformatService } from '../../../../util/dateformat.service';
import { SuiteShellService } from '@services/suite-shell.service';
import { etiquetaTipo } from '@services/tipos-crear.logic';

interface RequirementType {
  id: number;
  name: string;
  alias?: string | null;
  etiqueta?: string;
  fullname?: string;
  parentid: number | null;
  sub?: RequirementType[];
}

interface Requirement {
  id: number;
  name: string;
  requirementtypeid?: number;
  statusid?: number;
  date: string;
  status?: string;
  stage?: string;
  stagestatus?: string;
  creator?: string;
  owner?: string;
}

@Component({
    selector: 'app-add-activity-modal',
    templateUrl: './add-activity-modal.component.html',
    styleUrls: ['./add-activity-modal.component.scss'],
    providers: [
        { provide: NgbDateParserFormatter, useClass: DateformatService }
    ],
    standalone: false
})
export class AddActivityModalComponent implements OnInit {
	@Input() currentDate: string;
	@Input() reqId: number; // Opcional, puede venir del selector
	@Input() idUrlAdd: number;
	@Output() insertActivity = new EventEmitter<boolean>();
	newActivity: Activity = new Activity();
	// messages
	errorMessage: string = '';
	successMessage: string = '';
	private _success = new Subject<string>();
	private _error = new Subject<string>();
	asunto: string;
	activityTime: string = '';
	loading: boolean = false;
	dateActv: string;
	reminderObj: any;

	// Tipos de requerimiento y requerimientos
	requirementTypes: RequirementType[] = [];
	filteredRequirementTypes: RequirementType[] = [];
	requirements: Requirement[] = [];
	filteredRequirements: Requirement[] = [];
	selectedRequirementTypeId: number | null = null;
	selectedRequirementId: number | null = null;
	/** Wizard invertido: 1) formulario, 2) tipo, 3) requerimiento */
	currentStep: 1 | 2 | 3 = 1;
	loadingTypes: boolean = false;
	loadingRequirements: boolean = false;
	searchTypeText: string = '';
	searchRequirementText: string = '';
	requirementsTotal = 0;
	requirementsHasMore = false;
	readonly requirementsPageSize = 20;
	private searchTypeSubject = new Subject<string>();
	private searchRequirementSubject = new Subject<string>();

	// Colaboradores
	collaborators: Array<any> = [];
	multipleMultiSelect: string[] = [];
	public options: any = {
		multiple: true,
		width: '100%',
		tags: false
	};

	// Alertas
	alertsList: any[] = [];
	alertDates: NgbDateStruct[] = [];
	alertTimes: string[] = [];
	mindate: NgbDateStruct;
	maxdate: NgbDateStruct;

	// Links para calendarios externos
	linkGoogle: string = '';
	linkOutlook: string = '';

  constructor(
		private modalService: NgbModal,
		public activeModal: NgbActiveModal,
		public _requirements: RequirementService,
		private requirementTypesService: RequirementTypesService,
		private cdr: ChangeDetectorRef,
		private suite: SuiteShellService,
	) { }

  ngOnInit() {
		// transform the current date (solo visual; la fecha real se usa desde currentDate)
		this.dateActv = this.currentDate.substring(0, 10).split('-').reverse().toString().replace(',', '/').replace(',', '/');
		// load messages method
		this.messageSubmitForm();
		// load collaborators
		this.loadCollaborators();
		// Wizard invertido: siempre iniciamos en formulario.
		this.currentStep = 1;

		// Cargar tipos de requerimiento si no hay reqId (se usan en el paso 2)
		if (!this.reqId) {
			this.loadRequirementTypes();
		}
		this.updateAlertDateBounds();
		// Generate calendar links
		this.updateCalendarLinks();
		
		// Configurar búsqueda con debounce para tipos de requerimiento
		this.searchTypeSubject
			.pipe(debounceTime(300), distinctUntilChanged())
			.subscribe(searchText => {
				this.filterRequirementTypes(searchText);
			});
		
		// Búsqueda en servidor: mismo criterio que la grilla (json_data_search vía list/search?q)
		this.searchRequirementSubject
			.pipe(debounceTime(1000), distinctUntilChanged())
			.subscribe(searchText => {
				if (this.selectedRequirementTypeId) {
					this.loadRequirements(this.selectedRequirementTypeId, false, searchText);
				}
			});
		
		// Forzar detección de cambios
		setTimeout(() => {
			this.cdr.detectChanges();
		});
  }

	continueFromForm(): void {
		if (this.reqId) return; // si ya hay requerimiento, no hay pasos 2/3
		this.currentStep = 2;
	}

  /**
   * Actualizar links de calendarios cuando cambien los datos
   */
  updateCalendarLinks() {
		const subject = this.newActivity.subject || 'Nueva Actividad';
		const content = this.newActivity.content || '';

		// Link de Google Calendar
		this.linkGoogle = this.generateGoogleCalendarLink(subject, content);

		// Link de Outlook
		this.linkOutlook = this.generateOutlookLink(subject, content);
  }

  /**
   * Generar link de Google Calendar
   */
  generateGoogleCalendarLink(title: string, description: string): string {
		let link = "https://www.google.com/calendar/render?action=TEMPLATE";

		if (title) {
			link += `&text=${encodeURIComponent(title)}`;
		}
		if (description) {
			link += `&details=${encodeURIComponent(description)}`;
		}
		if (this.currentDate && this.activityTime) {
			const dateISO = this.setDateAndHourToISOString(this.currentDate, this.activityTime);
			const date1 = this.addHoursToDate(new Date(dateISO), 3);
			const date1ISO = date1.toISOString();
			const hourISO = date1ISO.substring(11, 19);
			const startDate = this.setDateAndHourToString(dateISO, hourISO);
			link += `&dates=${startDate}%2F${startDate}`;
		}

		return link;
  }

  /**
   * Generar link de Outlook
   */
  generateOutlookLink(title: string, description: string): string {
		let link = "https://outlook.live.com/calendar/0/deeplink/compose?";

		if (description) {
			link += `body=${encodeURIComponent(description)}`;
		}
		if (title) {
			link += `&subject=${encodeURIComponent(title)}`;
		}
		if (this.currentDate && this.activityTime) {
			const startDate = this.currentDate + 'T' + this.activityTime;
			link += `&startdt=${startDate}`;
		}

		link += "&path=%2Fcalendar%2Faction%2Fcompose&rru=addevent";

		return link;
  }

  /**
   * Convertir fecha y hora a ISO string
   */
  setDateAndHourToISOString(dateInput: string, hourInput: string): string {
		const fechaHora = dateInput + "T" + hourInput;
		const fechaLocal = new Date(fechaHora);
		return fechaLocal.toISOString();
  }

  /**
   * Convertir fecha y hora a string para Google Calendar
   */
  setDateAndHourToString(dateInput: string, hourInput: string): string {
		const date = dateInput.substring(0, 11).replace(/-/g, "");
		const hour = hourInput.replace(/:/g, "") + "Z";
		return date + hour;
  }

  /**
   * Agregar horas a una fecha
   */
  addHoursToDate(objDate: Date, intHours: number): Date {
		objDate.setHours(objDate.getHours() + intHours);
		return objDate;
  }
	submitActivity() {
		if (!this.validationActivity()) {
			return;
		}
		if (!this.validateAlerts()) {
			return;
		}
		// Convertir hora de formato HH:mm a HH:mm:ss
			const timeParts = this.activityTime.split(':');
			const activityTimeFormatted = `${timeParts[0]}:${timeParts[1]}:00`;

			this.loading = true;
			// recollecting data of activity
			let subject = this.newActivity.subject;

			// Usar el requerimiento seleccionado si no hay reqId predefinido
			const requirementId = this.reqId || this.selectedRequirementId;
			
			// Preparar datos en el formato correcto para el backend
			const data = {
				id: null,
				requirementid: requirementId,
				subject: subject.trim(),
				content: this.newActivity.content || '',
				date_activity: this.currentDate,
				hour_activity: activityTimeFormatted,
				alerts: this.alertsList || [],
				idsCollaborators: this.multipleMultiSelect && this.multipleMultiSelect.length > 0 ? this.multipleMultiSelect.map(id => parseInt(id)) : []
			};

			this._requirements.createActivityCalendar(data).subscribe( resp => {
				if (resp['error']) {
					this.loading = false;
					this._error.next('Error: No se pudo crear la actividad o ya existe uno con el mismo horario.');
				}	else {
					this.loading = false;
					this._success.next('Se realizó correctamente lo solicitado.');
					setTimeout(() => this.activeModal.dismiss(), 1600);
					setTimeout(() => this.insertActivity.emit(true), 1000);
				}
			});
			// here clean the class attribute
			this.newActivity = new Activity();
			this.newActivity.activity_date = '';
			this.newActivity.activity_time = '';
			this.newActivity.subject = '';
			this.newActivity.content = '';
			this.activityTime = '';
			this.multipleMultiSelect = [];
			this.alertsList = [];
			this.alertDates = [];
			this.alertTimes = [];
	}

	// validation activity.
	validationActivity(): boolean {
		// Validar que se haya seleccionado un requerimiento si no viene predefinido
		if (!this.reqId && !this.selectedRequirementId) {
			this._error.next('Error: Debe seleccionar un requerimiento.');
			return false;
		}
		
		// Usar el requerimiento seleccionado si no hay reqId predefinido
		const requirementId = this.reqId || this.selectedRequirementId;
		if (!requirementId) {
			this._error.next('Error: Debe seleccionar un requerimiento.');
			return false;
		}
		
		const subject = this.newActivity.subject;
		if ((subject === undefined || subject.trim() === '') && !this.activityTime) {
			this._error.next('Error: Falta llenar todos los campos o algún campo no está llenado correctamente.');
			return false;
		} else if ((subject === undefined || subject.trim() === '') && this.activityTime) {
			this._error.next('Error: Falta llenar el campo Asunto.');
			return false;
		} else if (subject !== undefined && !this.activityTime) {
			this._error.next('Error: Falta el campo Hora.');
			return false;
		} else {
			return true;
		}
	}
	// messages Method
	messageSubmitForm() {
		this._success.subscribe(message => this.successMessage = message);
		this._success.pipe (
			debounceTime(1500)
		).subscribe(() => this.successMessage = '');
		this._error.subscribe(message => this.errorMessage = message);
		this._error.pipe (
				debounceTime(1500)
		).subscribe(() => this.errorMessage = '');
	}
	/** Límites del datepicker de alertas según fecha de actividad (currentDate). */
	private updateAlertDateBounds(): void {
		const todayStruct = this.getTodayStruct();
		const activityStruct = this.parseActivityDateStruct();

		if (!activityStruct) {
			this.mindate = { ...todayStruct };
			this.maxdate = { ...todayStruct };
			return;
		}

		const activityYmd = this.structToYmd(activityStruct);
		const todayYmd = this.structToYmd(todayStruct);

		// Actividad hoy: la alerta solo puede ser hoy (hora antes de la actividad)
		if (activityYmd === todayYmd) {
			this.mindate = { ...todayStruct };
			this.maxdate = { ...todayStruct };
		} else {
			this.mindate = { ...todayStruct };
			this.maxdate = { ...activityStruct };
		}
	}

	private getTodayStruct(): NgbDateStruct {
		const now = new Date();
		return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
	}

	private parseActivityDateStruct(): NgbDateStruct | null {
		const ymd = (this.currentDate || '').substring(0, 10);
		if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
			return null;
		}
		const [y, m, d] = ymd.split('-').map(n => parseInt(n, 10));
		return { year: y, month: m, day: d };
	}

	private structToYmd(d: NgbDateStruct): string {
		const pad = (n: number) => String(n).padStart(2, '0');
		return `${d.year}-${pad(d.month)}-${pad(d.day)}`;
	}

	private normalizeTimeHHmm(time: string): string {
		if (!time) return '';
		const parts = time.split(':');
		return `${parts[0].padStart(2, '0')}:${(parts[1] || '00').padStart(2, '0')}`;
	}

	/** true si alerta es estrictamente anterior a la actividad */
	private isAlertBeforeActivity(alertDateYmd: string, alertTimeHHmm: string): boolean {
		const activityDate = (this.currentDate || '').substring(0, 10);
		const activityTime = this.normalizeTimeHHmm(this.activityTime);
		if (!alertDateYmd || !alertTimeHHmm || !activityDate || !activityTime) {
			return true;
		}
		const alertMs = new Date(`${alertDateYmd}T${alertTimeHHmm}:00`).getTime();
		const activityMs = new Date(`${activityDate}T${activityTime}:00`).getTime();
		return alertMs < activityMs;
	}

	validateAlerts(): boolean {
		for (let i = 0; i < this.alertsList.length; i++) {
			const alert = this.alertsList[i];
			const hasDate = !!alert.date_before_activity;
			const hasTime = !!this.alertTimes[i] && this.alertTimes[i] !== '';
			if (!hasDate && !hasTime) {
				continue;
			}
			if (!hasDate || !hasTime) {
				this._error.next('Error: Cada alerta debe tener fecha y hora.');
				return false;
			}
			const alertTime = this.normalizeTimeHHmm(this.alertTimes[i]);
			if (!this.isAlertBeforeActivity(alert.date_before_activity, alertTime)) {
				this._error.next('Error: La alerta debe ser anterior a la fecha y hora de la actividad.');
				return false;
			}
			const todayYmd = this.structToYmd(this.getTodayStruct());
			const activityYmd = (this.currentDate || '').substring(0, 10);
			if (activityYmd === todayYmd && alert.date_before_activity !== todayYmd) {
				this._error.next('Error: Si la actividad es hoy, la alerta también debe ser hoy.');
				return false;
			}
		}
		return true;
	}

	// Agregar alerta inline
	addAlert() {
		if (!this.validationAfterActivity()) {
			return;
		}
		// Agregar un objeto vacío para la nueva alerta
		this.alertsList.push({
			date_before_activity: null,
			hour_before_activity: null
		});
		this.alertDates.push(null);
		this.alertTimes.push(null);
	}

	// Actualizar fecha de alerta
	onAlertDateChange(index: number, date: NgbDateStruct | null) {
		if (!date) {
			return;
		}
		const dateStr = this.structToYmd(date);
		const activityYmd = (this.currentDate || '').substring(0, 10);
		const todayYmd = this.structToYmd(this.getTodayStruct());

		if (activityYmd === todayYmd && dateStr !== todayYmd) {
			this._error.next('Error: Si la actividad es hoy, la alerta debe ser hoy.');
			this.alertDates[index] = null;
			this.alertsList[index].date_before_activity = null;
			return;
		}
		if (dateStr > activityYmd) {
			this._error.next('Error: La fecha de la alerta no puede ser posterior a la de la actividad.');
			this.alertDates[index] = null;
			this.alertsList[index].date_before_activity = null;
			return;
		}

		this.alertsList[index].date_before_activity = dateStr;
		if (this.alertTimes[index]) {
			this.validateAlertDateTimeAt(index, true);
		}
	}

	// Actualizar hora de alerta
	onAlertTimeChange(index: number, time: string) {
		if (!time) {
			return;
		}
		const timeFormatted = time + ':00';
		this.alertsList[index].hour_before_activity = timeFormatted;
		this.validateAlertDateTimeAt(index, true);
	}

	private validateAlertDateTimeAt(index: number, showError: boolean): boolean {
		const alert = this.alertsList[index];
		const alertTime = this.alertTimes[index];
		if (!alert?.date_before_activity || !alertTime) {
			return true;
		}
		if (!this.isAlertBeforeActivity(alert.date_before_activity, this.normalizeTimeHHmm(alertTime))) {
			if (showError) {
				this._error.next('Error: La hora de la alerta debe ser anterior a la hora de la actividad.');
			}
			this.alertTimes[index] = null;
			this.alertsList[index].hour_before_activity = null;
			return false;
		}
		return true;
	}

	// Cargar colaboradores
	loadCollaborators() {
		this._requirements.getAllCollaboratorsData().subscribe({
			next: (response: any) => {
				console.log('Colaboradores response:', response);
				if (response && response.result) {
					// ng-select2 requiere un array con objetos que tengan 'id' y 'text'
					this.collaborators = response.result;
					console.log('Colaboradores cargados para ng-select2:', this.collaborators);
					// Forzar detección de cambios
					this.cdr.detectChanges();
				}
			},
			error: (error) => {
				console.error('Error al cargar colaboradores:', error);
				this._error.next('Error al cargar los colaboradores.');
			}
		});
	}

	// Eliminar alerta de la lista
	removeAlert(index: number) {
		this.alertsList.splice(index, 1);
		this.alertDates.splice(index, 1);
		this.alertTimes.splice(index, 1);
	}

	// Formatear fecha de alerta para mostrar
	formatAlertDate(dateString: string): string {
		if (!dateString) return '';
		const date = new Date(dateString);
		return date.toLocaleDateString('es-ES', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit'
		});
	}
	validationAfterActivity(): boolean {
		const subject = this.newActivity.subject;
		if ((subject === undefined || subject.trim() === '') && !this.activityTime) {
			this._error.next('Error: Antes de crear una alerta, debe llenar todos los campos obligatorios.');
			return false;
		} else if ((subject === undefined || subject.trim() === '') && this.activityTime) {
			this._error.next('Error: Antes de crear una alerta, debe llenar el campo Asunto.');
			return false;
		} else if (subject !== undefined && !this.activityTime) {
			this._error.next('Error: Antes de crear una alerta, debe llenar el campo Hora.');
			return false;
		} else {
			return true;
		}
	}

	/**
	 * Cargar tipos de requerimientos
	 */
	loadRequirementTypes(): void {
		this.loadingTypes = true;
		this.errorMessage = '';
		
		this.requirementTypesService.getRequirementTypes().subscribe({
			next: (response: any) => {
				if (response && response.result) {
					// Aplanar la estructura jerárquica de tipos de requerimiento
					this.requirementTypes = this.flattenRequirementTypes(response.result);
					this.filteredRequirementTypes = [...this.requirementTypes];
				}
				this.loadingTypes = false;
				this.cdr.detectChanges();
			},
			error: (error) => {
				console.error('Error al cargar tipos de requerimientos:', error);
				this._error.next('Error al cargar los tipos de requerimientos. Por favor, intente nuevamente.');
				this.loadingTypes = false;
			}
		});
	}

	/**
	 * Aplanar la estructura jerárquica de tipos de requerimiento
	 */
	private flattenRequirementTypes(types: RequirementType[], parentFullname: string = ''): RequirementType[] {
		let result: RequirementType[] = [];
		
		const enSuite = this.suite.cara === 'suite';
		types.forEach(type => {
			const etiqueta = etiquetaTipo(type.name, type.alias, enSuite);
			const fullname = parentFullname ? `${parentFullname} > ${etiqueta}` : etiqueta;
			result.push({
				id: type.id,
				name: type.name,
				alias: type.alias,
				etiqueta,
				fullname: fullname,
				parentid: type.parentid,
				sub: type.sub
			});
			
			if (type.sub && type.sub.length > 0) {
				result = result.concat(this.flattenRequirementTypes(type.sub, fullname));
			}
		});
		
		return result;
	}

	/**
	 * Filtrar tipos de requerimiento por búsqueda
	 */
	filterRequirementTypes(searchText: string): void {
		if (!searchText || searchText.trim() === '') {
			this.filteredRequirementTypes = [...this.requirementTypes];
			return;
		}
		
		const search = searchText.toLowerCase();
		this.filteredRequirementTypes = this.requirementTypes.filter(type =>
			(type.fullname && type.fullname.toLowerCase().includes(search)) ||
			type.name.toLowerCase().includes(search) ||
			(type.etiqueta || '').toLowerCase().includes(search)
		);
	}

	/**
	 * Manejar cambio en búsqueda de tipos
	 */
	onSearchTypeChange(searchText: string): void {
		this.searchTypeSubject.next(searchText);
	}

	/**
	 * Seleccionar tipo de requerimiento
	 */
	selectRequirementType(typeId: number): void {
		this.selectedRequirementTypeId = typeId;
		this.selectedRequirementId = null;
		this.searchRequirementText = '';
		this.currentStep = 3;
		this.loadRequirements(typeId, false, '');
	}

	/**
	 * Cargar requerimientos por tipo (paginado).
	 * Búsqueda: POST list/search con search.q → ILIKE en json_data_search (análogo a grilla getdatav2 + raw_value).
	 */
	loadRequirements(typeId: number, append = false, searchText = ''): void {
		this.loadingRequirements = true;
		const start = append ? this.requirements.length : 0;
		const search: Record<string, unknown> = { requirementtypeid: typeId };
		const q = (searchText || '').trim();
		if (q) {
			search['q'] = q; // backend: applyRequirementJsonDataSearchWhere (misma SQL que la grilla)
		}

		const body = {
			search,
			order: { date: 'DESC' },
			start,
			length: this.requirementsPageSize
		};

		this._requirements.searchRequirements(body).subscribe({
			next: (response: any) => {
				let page: Requirement[] = [];
				let total = 0;
				if (response?.result?.data) {
					page = response.result.data;
					total = Number(response.result.recordsTotal) || page.length;
				} else if (Array.isArray(response?.result)) {
					page = response.result;
					total = page.length;
				}

				this.requirements = append ? [...this.requirements, ...page] : page;
				this.filteredRequirements = [...this.requirements];
				this.requirementsTotal = total;
				this.requirementsHasMore = this.requirements.length < this.requirementsTotal;
				this.loadingRequirements = false;
				this.cdr.detectChanges();
			},
			error: () => {
				this._error.next('Error al cargar los requerimientos. Por favor, intente nuevamente.');
				this.loadingRequirements = false;
			}
		});
	}

	loadMoreRequirements(): void {
		if (!this.selectedRequirementTypeId || !this.requirementsHasMore || this.loadingRequirements) {
			return;
		}
		this.loadRequirements(this.selectedRequirementTypeId, true, this.searchRequirementText);
	}

	clearRequirementSelection(): void {
		this.selectedRequirementId = null;
		this.searchRequirementText = '';
		if (this.selectedRequirementTypeId) {
			this.loadRequirements(this.selectedRequirementTypeId, false, '');
		}
		this.cdr.detectChanges();
	}

	getSelectedRequirement(): Requirement | null {
		const id = this.selectedRequirementId;
		if (!id) return null;
		return this.requirements.find(r => r.id === id) || null;
	}

	/**
	 * Manejar cambio en búsqueda de requerimientos
	 */
	onSearchRequirementChange(searchText: string): void {
		this.searchRequirementSubject.next(searchText);
	}

	/**
	 * Seleccionar requerimiento
	 */
	selectRequirement(requirementId: number): void {
		this.selectedRequirementId = requirementId;
		this.currentStep = 3;
		this.cdr.detectChanges();
	}

	/**
	 * Obtener el nombre del tipo de requerimiento seleccionado
	 */
	getSelectedRequirementTypeName(): string {
		if (!this.selectedRequirementTypeId) return '';
		const type = this.requirementTypes.find(t => t.id === this.selectedRequirementTypeId);
		return type ? (type.fullname || type.name) : '';
	}

	/**
	 * Obtener el nombre del requerimiento seleccionado
	 */
	getSelectedRequirementName(): string {
		const id = this.selectedRequirementId || this.reqId;
		if (!id) return '';
		const req = this.requirements.find(r => r.id === id);
		return req ? req.name : '';
	}

	goBack(): void {
		if (this.reqId) return;
		if (this.currentStep === 3 && this.selectedRequirementId) {
			this.clearRequirementSelection();
			return;
		}
		if (this.currentStep === 3) this.currentStep = 2;
		else if (this.currentStep === 2) this.currentStep = 1;
	}

	getModalStepTitle(): string {
		if (this.reqId) return 'Crear actividad';
		switch (this.currentStep) {
			case 1: return 'Datos de Actividad y Alerta';
			case 2: return 'Tipo de requerimiento';
			case 3: return 'Asociar requerimiento';
			default: return 'Crear actividad y alertas';
		}
	}

	/**
	 * Formatear fecha para mostrar
	 */
	formatDate(dateString: string): string {
		if (!dateString) return '';
		const date = new Date(dateString);
		return date.toLocaleDateString('es-ES', {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	}

}
